import { takeEvery, put, call, all, select } from 'redux-saga/effects';
import ActionTypes from '../../../../Data/ActionTypes';
import getCityAndStateForPostcodeAndZipQuery from './getCityAndStateForPostcodeAndZipQuery';
import { populateFormFieldSaga } from '../../../../Hooks/actionCreators';
import logToConsole from '../../../../Utils/logToConsole';

/**
 * Populates all components that have the same mappedQuestionId as the target field
 * and are not filled
 */
export function* populateMatchingCityStateFields({
  components,
  mappedQuestionId,
  value,
  countryCode,
  iFrameId,
  formId,
}) {
  const matchingComponents = Object.values(components).filter(
    (component) => component.component.mappedQuestionId === mappedQuestionId
  );

  if (!matchingComponents) {
    return;
  }

  yield all(
    matchingComponents.map((component) =>
      put(
        populateFormFieldSaga({
          countryCode,
          value,
          iFrameId,
          formId,
          component,
        })
      )
    )
  );
}

/**
 * Populates city and state fields with the geo data obtained from the API.
 */
function* populateCityStateFields({
  geoData,
  components,
  countryCode,
  formId,
  iFrameId,
}) {
  if (!geoData) {
    return;
  }

  // Populate city field
  if (geoData.city) {
    yield populateMatchingCityStateFields({
      components,
      mappedQuestionId: 'primary_address_city',
      value: geoData.city,
      countryCode,
      iFrameId,
      formId,
    });
  }

  // Populate state field
  if (geoData.state) {
    yield populateMatchingCityStateFields({
      components,
      mappedQuestionId: 'primary_address_state',
      value: geoData.stateCode,
      countryCode,
      iFrameId,
      formId,
    });
  }
}

/**
 * Handles the behaviour following validation of a zip code with triggerCityAndStateAutofill enabled:
 * - Obtains city and state data for the provided zip code and country
 * - Prefills city and state fields if they exist and are not already filled
 */
export function* handleZipCodeProvidedWorker(action) {
  const { postcode, country, formId, iFrameId } = action.payload;
  const { components } = yield select();

  try {
    const geoData = yield call(getCityAndStateForPostcodeAndZipQuery, {
      variables: {
        postcode,
        country,
      },
    });

    yield populateCityStateFields({
      geoData,
      components,
      countryCode: country,
      formId,
      iFrameId,
    });
  } catch (error) {
    // Fail silently if geo lookup fails - don't break the user experience
    logToConsole.warn(
      'Failed to lookup city and state for postcode:',
      postcode,
      error
    );
  }
}

/**
 * Redux Watcher
 */
export default function* handleZipCodeProvidedWatcher() {
  yield takeEvery(
    ActionTypes.HANDLE_ZIP_CODE_PROVIDED,
    handleZipCodeProvidedWorker
  );
}
